import { languages } from '../languages'

export const AdminPermission = 'admin' as const
export const ReadPermissionsPermission = 'readPermissions' as const
export const WritePermissionsPermission = 'writePermissions' as const
export const ReadBetaAccessPermission = 'readBetaAccess' as const
export const WriteBetaAccessPermission = 'writeBetaAccess' as const
export const WriteTranslationPermissionsPermission = 'writeTranslationPermissions' as const
export const ReadTranslationsPermission = 'readTranslations' as const
export const ReadCustomersPermission = 'readCustomers' as const
export const ReadCustomersSecurityPermission = 'readCustomersSecurity' as const
export const WriteCustomerSecurityPermission = 'writeCustomerSecurity' as const
export const ReadRegionPermission = 'readRegion' as const
export const WriteRegionPermission = 'writeRegion' as const
export const ReadSubscriptionsPermission = 'readSubscriptions' as const
export const WritePaidSubscriptionsPermission = 'writePaidSubscriptions' as const
export const WriteFreeSubscriptionsPermission = 'writeFreeSubscriptions' as const
export const WriteSubscriptionsPermission = 'writeSubscriptions' as const
export const CreateSubscriptionsPermission = 'createSubscriptions' as const
export const ReadPartnerOAuthProjectsPermission = 'readPartnerOAuthProjects' as const
export const WritePartnerOAuthProjectsPermission = 'writePartnerOAuthProjects' as const
export const ReadCustomerFeedbackPermission = 'readCustomerFeedback' as const
export const ReadCustomerMetricsPermission = 'readCustomerMetrics' as const
export const ReadSubscriptionMetricsPermission = 'readSubscriptionMetrics' as const
export const ReadFeatureMetricsPermission = 'readFeatureMetrics' as const
export const ReadSystemMetricPermission = 'readSystemMetric' as const
export const ReadSystemConfigPermission = 'readSystemConfig' as const
export const WriteSystemConfigPermission = 'writeSystemConfig' as const

export const systemPermissions = [
	AdminPermission,
	ReadPermissionsPermission,
	WritePermissionsPermission,
	ReadBetaAccessPermission,
	WriteBetaAccessPermission,
	WriteTranslationPermissionsPermission,
	ReadTranslationsPermission,
	ReadCustomersPermission,
	ReadCustomersSecurityPermission,
	WriteCustomerSecurityPermission,
	ReadRegionPermission,
	WriteRegionPermission,
	ReadSubscriptionsPermission,
	WritePaidSubscriptionsPermission,
	WriteFreeSubscriptionsPermission,
	WriteSubscriptionsPermission,
	CreateSubscriptionsPermission,
	ReadPartnerOAuthProjectsPermission,
	WritePartnerOAuthProjectsPermission,
	ReadCustomerFeedbackPermission,
	ReadCustomerMetricsPermission,
	ReadSubscriptionMetricsPermission,
	ReadFeatureMetricsPermission,
	ReadSystemMetricPermission,
	ReadSystemConfigPermission,
	WriteSystemConfigPermission
] as const

const translationPermissionLanguages = [...languages, { value: 'eu', label: 'Euskara - Basque' }, { value: 'sn', label: 'Shona - Shona (Zimbabwe)' }] as const

export const systemTranslationLanguageCodes = translationPermissionLanguages.map(({ value }) => value)

export const WriteTranslationsPermissionPrefix = 'writeTranslations:' as const
export type SystemTranslationLanguageCode = (typeof systemTranslationLanguageCodes)[number]
export type WriteTranslationsPermissionKey = `${typeof WriteTranslationsPermissionPrefix}${SystemTranslationLanguageCode}`

const getWriteTranslationsPermissionKey = (languageCode: SystemTranslationLanguageCode): WriteTranslationsPermissionKey =>
	`${WriteTranslationsPermissionPrefix}${languageCode}`

export const writeTranslationsPermissions = systemTranslationLanguageCodes.map(getWriteTranslationsPermissionKey)

export const userPermissionsAdminPagePermissions = [
	ReadPermissionsPermission,
	WritePermissionsPermission,
	WriteBetaAccessPermission,
	WriteTranslationPermissionsPermission
] as const

export const userPermissions = [...systemPermissions, ...writeTranslationsPermissions] as const

/* Account-filter sentinel matching users that hold at least one translation language permission */
export const AnyTranslationLanguagePermissionsFilter = 'anyTranslationLanguage' as const

export const permissionsInheritanceMap: Partial<Record<(typeof userPermissions)[number], (typeof userPermissions)[number][]>> = {
	[ReadPermissionsPermission]: [WritePermissionsPermission, AdminPermission],
	[WritePermissionsPermission]: [AdminPermission],
	[ReadBetaAccessPermission]: [WriteBetaAccessPermission],
	[ReadTranslationsPermission]: [WriteTranslationPermissionsPermission],
	[ReadCustomersSecurityPermission]: [WriteCustomerSecurityPermission],
	[ReadRegionPermission]: [WriteRegionPermission],
	[ReadSubscriptionsPermission]: [WritePaidSubscriptionsPermission, WriteFreeSubscriptionsPermission, WriteSubscriptionsPermission],
	[WritePaidSubscriptionsPermission]: [WriteSubscriptionsPermission],
	[WriteFreeSubscriptionsPermission]: [WriteSubscriptionsPermission],
	[ReadPartnerOAuthProjectsPermission]: [WritePartnerOAuthProjectsPermission],
	[ReadSystemConfigPermission]: [WriteSystemConfigPermission]
}

export const systemPermissionDefaultValue: boolean = false

type SystemPermissionsData = {
	[key in (typeof userPermissions)[number]]: { description: string; label: string; permissionId: number; defaultValue: typeof systemPermissionDefaultValue }
}

const writeTranslationsPermissionsData: { [key in WriteTranslationsPermissionKey]: SystemPermissionsData[key] } = translationPermissionLanguages.reduce(
	(acc, { value, label }, index) => {
		const permissionLabel = `WriteTranslations${label.split('-')[1].trim()}`
		return {
			...acc,
			[getWriteTranslationsPermissionKey(value)]: {
				description: `Can edit ${permissionLabel} translations`,
				label: permissionLabel,
				permissionId: 100 + index,
				defaultValue: systemPermissionDefaultValue
			}
		}
	},
	{} as { [key in WriteTranslationsPermissionKey]: SystemPermissionsData[key] }
)

export const systemPermissionsData: SystemPermissionsData = {
	[AdminPermission]: {
		description: 'Super user permission; allows Write Permissions to show access to all permissions',
		label: 'Admin',
		permissionId: 0,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadPermissionsPermission]: {
		description: 'Can see the list of who has what permissions',
		label: 'ReadPermissions',
		permissionId: 7,
		defaultValue: systemPermissionDefaultValue
	},
	[WritePermissionsPermission]: {
		description: 'Can grant permissions up to the level of permissions already granted',
		label: 'WritePermissions',
		permissionId: 8,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadBetaAccessPermission]: {
		description: 'Can see beta releases and beta content',
		label: 'ReadBetaAccess',
		permissionId: 4,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteBetaAccessPermission]: {
		description: 'Can assign beta access',
		// It doesn't require WritePermissionsPermission to grant ReadBetaAccessPermission
		label: 'WriteBetaAccess',
		permissionId: 9,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteTranslationPermissionsPermission]: {
		description: 'Can assign permission to edit specific languages',
		// It doesn't require WritePermissionsPermission to grant Individual Language Translation Permissions
		label: 'WriteTranslationPermissions',
		permissionId: 10,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadTranslationsPermission]: {
		description: 'Can see existing translations',
		label: 'ReadTranslations',
		permissionId: 1,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadCustomersPermission]: {
		description: 'Can see basic information about users and organisations',
		label: 'ReadCustomers',
		permissionId: 11,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadCustomersSecurityPermission]: {
		description: 'Can see information about user sessions and security configs',
		label: 'ReadCustomerSecurity',
		permissionId: 12,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteCustomerSecurityPermission]: {
		description: 'Can reset passwords, lock out users, invalidate sessions, and make similar security changes',
		label: 'WriteCustomerSecurity',
		permissionId: 13,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadRegionPermission]: {
		description: 'Can see which region users and organizations reside in',
		label: 'ReadRegion',
		permissionId: 14,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteRegionPermission]: {
		description: 'Can edit which region users and organizations reside in',
		label: 'WriteRegion',
		permissionId: 15,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadSubscriptionsPermission]: {
		description: 'Can see payment and subscription information',
		label: 'ReadSubscriptions',
		permissionId: 6,
		defaultValue: systemPermissionDefaultValue
	},
	[WritePaidSubscriptionsPermission]: {
		description: 'Can edit and assign non-free subscriptions to users and organizations',
		label: 'WritePaidSubscriptions',
		permissionId: 16,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteFreeSubscriptionsPermission]: {
		description: 'Can edit and assign free subscriptions to users and organizations',
		label: 'WriteFreeSubscriptions',
		permissionId: 23,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteSubscriptionsPermission]: {
		description:
			'Can create subscriptions, edit and assign any tier subscription to users and organizations, and reset trials. Inherits write free and write paid subscription permissions.',
		label: 'WriteSubscriptions',
		permissionId: 5,
		defaultValue: systemPermissionDefaultValue
	},
	[CreateSubscriptionsPermission]: {
		description: 'Can create new subscription pricing options only',
		label: 'CreateSubscriptions',
		permissionId: 17,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadPartnerOAuthProjectsPermission]: {
		description: 'Can view partner OAuth projects',
		label: 'ReadPartnerOAuthProjects',
		permissionId: 24,
		defaultValue: systemPermissionDefaultValue
	},
	[WritePartnerOAuthProjectsPermission]: {
		description: 'Can create and edit partner OAuth projects',
		label: 'WritePartnerOAuthProjects',
		permissionId: 25,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadCustomerFeedbackPermission]: {
		description: 'Can see user feedback',
		label: 'ReadCustomerFeedback',
		permissionId: 18,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadCustomerMetricsPermission]: {
		description: 'Can see basic customer usage metrics',
		label: 'ReadCustomerMetrics',
		permissionId: 19,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadSubscriptionMetricsPermission]: {
		description: 'Can see subscription metrics and reports',
		label: 'ReadSubscriptionMetrics',
		permissionId: 3,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadFeatureMetricsPermission]: {
		description: 'Can see feature metrics',
		label: 'ReadFeatureMetrics',
		permissionId: 2,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadSystemMetricPermission]: {
		description: 'Can see system metrics',
		label: 'ReadSystemMetric',
		permissionId: 20,
		defaultValue: systemPermissionDefaultValue
	},
	[ReadSystemConfigPermission]: {
		description: 'Can see system config options',
		label: 'ReadSystemConfig',
		permissionId: 21,
		defaultValue: systemPermissionDefaultValue
	},
	[WriteSystemConfigPermission]: {
		description: 'Can edit system config options',
		label: 'WriteSystemConfig',
		permissionId: 22,
		defaultValue: systemPermissionDefaultValue
	},
	...writeTranslationsPermissionsData
}

export const userPermissionsData = systemPermissionsData
